import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useLocation, useMatches, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

function BreadcrumbTrail({ contextOptions = [] }) {
	const { pathname, search, state } = useLocation();
	const routeMatches = useMatches();
	const navigate = useNavigate();
	const menuItems = useSelector((store) => store.menu.menuItems);
	const [processFormCaseNo, setProcessFormCaseNo] = useState(() => {
		const storedStageId = sessionStorage.getItem('breadcrumb:processFormStageId') ?? '';
		const activeStageId = String(state?.stageId ?? '');
		if (!activeStageId || storedStageId !== activeStageId) return '';
		return sessionStorage.getItem('breadcrumb:processFormCaseNo') ?? '';
	});

	const breadcrumbScrollRef = useRef(null);
	const breadcrumbDragRef = useRef({
		isDragging: false,
		startX: 0,
		startScrollLeft: 0,
		didDrag: false,
	});

	const formatSegmentLabel = (segment) => {
		const decoded = decodeURIComponent(segment);
		const isHumanLabel = decoded.includes(' ');
		const normalized = isHumanLabel ? decoded.replace(/_+/g, ' ') : decoded.replace(/[-_]+/g, ' ');
		return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
	};

	const createTabConfig = {
		all: { label: 'All' },
		recent: { label: 'Recent' },
		frequent: { label: 'Frequent' },
		available: { label: 'Available' },
	};
	const reportTabConfig = {
		standard: { label: 'Standard' },
		custom: { label: 'Custom' },
	};

	const normalizeMenuKey = (value) =>
		String(value ?? '')
			.trim()
			.toLowerCase()
			.replace(/^\/+|\/+$/g, '')
			.replace(/\s+/g, '-');

	const decodeBase64UrlSegment = (segment) => {
		try {
			if (typeof atob !== 'function') return null;
			const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
			const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
			return atob(padded);
		} catch {
			return null;
		}
	};

	const parseEncodedPayloadSegment = (segment) => {
		const decoded = decodeBase64UrlSegment(decodeURIComponent(segment));
		if (!decoded) return null;

		const payload = decoded.trim();
		if (!payload) return null;

		const looksLikeJson = (payload.startsWith('{') && payload.endsWith('}')) || (payload.startsWith('[') && payload.endsWith(']'));
		if (!looksLikeJson) return null;

		try {
			return JSON.parse(payload);
		} catch {
			return null;
		}
	};

	const getPayloadLabel = (payload) => {
		if (!payload) return null;

		const normalize = (value) => {
			if (typeof value !== 'string') return null;
			const trimmed = value.trim();
			return trimmed ? formatSegmentLabel(trimmed) : null;
		};

		if (typeof payload === 'string') return normalize(payload);
		if (Array.isArray(payload)) {
			for (const item of payload) {
				const label = getPayloadLabel(item);
				if (label) return label;
			}
			return null;
		}

		if (typeof payload !== 'object') return null;

		const preferredKeys = ['processName', 'name', 'displayName', 'title', 'label', 'caseName'];
		for (const key of preferredKeys) {
			const label = normalize(payload[key]);
			if (label) return label;
		}

		for (const [key, value] of Object.entries(payload)) {
			if (key.toLowerCase().includes('name')) {
				const label = normalize(value);
				if (label) return label;
			}
		}

		return null;
	};

	const getDecodedSegmentLabel = (segment) => {
		const payload = parseEncodedPayloadSegment(segment);
		return getPayloadLabel(payload);
	};

	const isTechnicalSegment = (segment) => {
		const value = decodeURIComponent(segment);
		return (
			/^\d+$/.test(value) ||
			/^[0-9a-f]{24}$/i.test(value) ||
			/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value) ||
			/^[0-9a-f]{16,}$/i.test(value) ||
			Boolean(parseEncodedPayloadSegment(segment))
		);
	};

	const getCreateTabKey = (searchValue) => {
		const tabKey = new URLSearchParams(searchValue ?? '').get('tab')?.toLowerCase?.() ?? 'all';
		return createTabConfig[tabKey] ? tabKey : 'all';
	};

	const getCreateTabLabel = (searchValue) => createTabConfig[getCreateTabKey(searchValue)]?.label ?? createTabConfig.all.label;
	const getReportTabKey = (searchValue) => {
		const tabKey = new URLSearchParams(searchValue ?? '').get('tab')?.toLowerCase?.() ?? 'standard';
		return reportTabConfig[tabKey] ? tabKey : 'standard';
	};
	const getReportTabLabel = (searchValue) => reportTabConfig[getReportTabKey(searchValue)]?.label ?? reportTabConfig.standard.label;

	const getStoredCreateSourcePath = () => sessionStorage.getItem('breadcrumb:createSourcePath') ?? '';
	const getStoredReportSourcePath = () => sessionStorage.getItem('breadcrumb:reportSourcePath') ?? '';

	const resolveCreateSourcePath = (sourcePath) => {
		const normalizedSourcePath = sourcePath
			.split('?')[0]
			.split('#')[0]
			.replace(/^\/+|\/+$/g, '')
			.toLowerCase();
		const isCreateSource = normalizedSourcePath === 'create' || normalizedSourcePath === 'solutions-catalog';

		return {
			isCreateSource,
			createSourcePath: isCreateSource ? sourcePath || '/manage?tab=all' : '/manage?tab=all',
		};
	};

	const resolveReportSourcePath = (sourcePath) => {
		const normalizedSourcePath = sourcePath
			.split('?')[0]
			.split('#')[0]
			.replace(/^\/+|\/+$/g, '')
			.toLowerCase();
		const isReportSource = normalizedSourcePath === 'reports' || normalizedSourcePath.startsWith('reports/');

		return {
			isReportSource,
			reportSourcePath: isReportSource ? sourcePath || '/reports?tab=standard' : '/reports?tab=standard',
		};
	};

	const menuScopedSegments = useMemo(() => {
		const pathSegments = pathname.split('/').filter(Boolean);
		const isModuleScoped = pathSegments[0] === 'modules' && Boolean(pathSegments[1]);
		let scopedSegments = isModuleScoped ? pathSegments.slice(2) : pathSegments;

		if (isModuleScoped && scopedSegments.length === 0) {
			scopedSegments = ['insights'];
		}

		return scopedSegments;
	}, [pathname]);

	const menuGroupPrefixItems = useMemo(() => {
		const firstSegment = menuScopedSegments[0];
		if (!firstSegment || !Array.isArray(menuItems) || menuItems.length === 0) return [];

		const firstSegmentKey = normalizeMenuKey(firstSegment);

		for (const parent of menuItems) {
			const children = Array.isArray(parent?.children) ? parent.children : [];
			for (const child of children) {
				const childDisplayName = child?.displayName ?? '';
				const childCandidates = [childDisplayName, childDisplayName.replace(/\s+/g, ''), child?.name, child?.pathName, child?.path]
					.map(normalizeMenuKey)
					.filter(Boolean);

				if (!childCandidates.includes(firstSegmentKey)) continue;

				const groupLabel = parent?.displayName ? formatSegmentLabel(parent.displayName) : 'Navigation';
				return [
					{
						id: `group-prefix-${parent?.id ?? parent?.name ?? 'menu'}`,
						label: groupLabel,
						clickable: false,
					},
				];
			}
		}

		return [];
	}, [menuItems, menuScopedSegments]);

	const pathBreadcrumbItems = useMemo(() => {
		const pathSegments = pathname.split('/').filter(Boolean);
		const isModuleScoped = pathSegments[0] === 'modules' && Boolean(pathSegments[1]);
		const scopePrefix = isModuleScoped ? pathSegments.slice(0, 2) : [];
		let scopedSegments = isModuleScoped ? pathSegments.slice(2) : pathSegments;

		if (isModuleScoped && scopedSegments.length === 0) {
			scopedSegments = ['insights'];
		}

		return scopedSegments.reduce((items, segment, index) => {
			const decodedLabel = getDecodedSegmentLabel(segment);
			if (isTechnicalSegment(segment) && !decodedLabel) return items;
			const shouldHideControlsTrailingSegment = !isModuleScoped && scopedSegments[0] === 'controls' && index === 2;
			if (shouldHideControlsTrailingSegment) return items;
			const isControlsContextSegment = !isModuleScoped && scopedSegments[0] === 'controls' && index === 1 && Boolean(scopedSegments[2]);
			const breadcrumbSegments = isControlsContextSegment ? scopedSegments.slice(0, 3) : scopedSegments.slice(0, index + 1);
			const fullPath = `/${[...scopePrefix, ...breadcrumbSegments].join('/')}`;
			items.push({
				id: `path-${segment}-${index}`,
				label: decodedLabel ?? formatSegmentLabel(segment),
				path: `${fullPath}${search}`,
				clickable: true,
			});
			return items;
		}, []);
	}, [pathname, search]);

	const createPageBreadcrumbItems = useMemo(() => {
		if (!['/manage', '/solutions-catalog'].includes(pathname)) return [];

		const activeTabKey = getCreateTabKey(search);
		const activeTabLabel = getCreateTabLabel(search);

		return [
			{
				id: 'create-root',
				label: 'Create',
				path: '/manage?tab=all',
				clickable: activeTabKey !== 'all',
			},
			{
				id: `create-tab-${activeTabKey}`,
				label: activeTabLabel,
				clickable: false,
			},
		];
	}, [pathname, search]);

	const reportsPageBreadcrumbItems = useMemo(() => {
		if (pathname !== '/reports') return [];

		const activeTabKey = getReportTabKey(search);
		const activeTabLabel = getReportTabLabel(search);

		return [
			{
				id: 'reports-root',
				label: 'Reports',
				path: '/reports?tab=standard',
				clickable: activeTabKey !== 'standard',
			},
			{
				id: `reports-tab-${activeTabKey}`,
				label: activeTabLabel,
				path: `/reports?tab=${activeTabKey}`,
				clickable: true,
			},
		];
	}, [pathname, search]);

	const reportDetailBreadcrumbItems = useMemo(() => {
		if (!pathname.startsWith('/reports/') || pathname === '/reports') return [];

		const sourceFromState = typeof state?.pathname === 'string' ? state.pathname : '';
		const sourceFromFallback = getStoredReportSourcePath() || (sessionStorage.getItem('breadcrumb:lastNonProcessPath') ?? '');
		const source = sourceFromState || sourceFromFallback;
		const { reportSourcePath } = resolveReportSourcePath(source);

		const pathSegments = pathname.split('/').filter(Boolean);
		const secondSegment = pathSegments[1]?.toLowerCase?.() ?? '';
		const tabKey =
			secondSegment === 'standard' || secondSegment === 'custom'
				? secondSegment
				: getReportTabKey(reportSourcePath.includes('?') ? reportSourcePath.split('?')[1] : '');
		const baseItems = secondSegment === 'standard' || secondSegment === 'custom' ? pathBreadcrumbItems.slice(2) : pathBreadcrumbItems.slice(1);
		const normalizedLeafItems = baseItems.map((item, index) => ({
			...item,
			clickable: index < baseItems.length - 1 ? item.clickable : false,
		}));

		return [
			{
				id: 'reports-detail-root',
				label: 'Reports',
				path: `/reports?tab=${tabKey}`,
				clickable: true,
			},
			{
				id: `reports-detail-tab-${tabKey}`,
				label: reportTabConfig[tabKey]?.label ?? reportTabConfig.standard.label,
				path: `/reports?tab=${tabKey}`,
				clickable: true,
			},
			...normalizedLeafItems,
		];
	}, [pathBreadcrumbItems, pathname, state?.pathname]);

	const processFormOriginBreadcrumbItems = useMemo(() => {
		if (pathname !== '/process-form') return [];

		const sourceFromState = typeof state?.pathname === 'string' ? state.pathname : '';
		const sourceFromFallback = getStoredCreateSourcePath() || (sessionStorage.getItem('breadcrumb:lastNonProcessPath') ?? '');
		const source = sourceFromState || sourceFromFallback;
		const { isCreateSource, createSourcePath } = resolveCreateSourcePath(source);
		const createSourceTabLabel = getCreateTabLabel(source.includes('?') ? source.split('?')[1] : '');

		if (!source) return [];

		const normalized = source
			.split('?')[0]
			.split('#')[0]
			.replace(/^\/+|\/+$/g, '')
			.toLowerCase();
		const normalizedAliasMap = {
			inprogress: 'in-progress',
		};
		const sourceKey = normalizedAliasMap[normalized] ?? normalized;
		const sourcePath = `/${sourceKey}`;
		const sectionTitleByParentCode = {
			'lyn-process-worklist': 'Worklist',
			'lyn-process-my-cases': 'My Tasks',
			'lyn-process-governance': 'Governance',
		};

		if (Array.isArray(menuItems) && menuItems.length > 0) {
			for (const parent of menuItems) {
				const children = Array.isArray(parent?.children) ? parent.children : [];
				for (const child of children) {
					const childDisplayName = child?.displayName ?? '';
					const childNameFromDisplay = childDisplayName.toLowerCase().replace(/\s+/g, '-');
					const childNameFromDisplayCollapsed = childDisplayName.toLowerCase().replace(/\s+/g, '');
					const childName = (child?.name ?? '').toLowerCase();
					const childPathName = child?.pathName?.toLowerCase?.() ?? '';
					const childRoutePath = child?.path?.toLowerCase?.() ?? '';

					const matched =
						sourceKey === childNameFromDisplay ||
						sourceKey === childNameFromDisplayCollapsed ||
						sourceKey === childName ||
						sourceKey === childPathName ||
						sourceKey === childRoutePath;

					if (!matched) continue;

					const groupLabel =
						sectionTitleByParentCode[parent?.name] ?? (parent?.displayName ? formatSegmentLabel(parent.displayName) : 'Navigation');
					const childLabel = childDisplayName ? formatSegmentLabel(childDisplayName) : formatSegmentLabel(sourceKey);

					return [
						{
							id: `process-origin-group-${parent?.id ?? parent?.name ?? 'menu'}`,
							label: groupLabel,
							path: sourcePath,
							clickable: true,
						},
						{
							id: `process-origin-child-${child?.id ?? child?.name ?? sourceKey}`,
							label: childLabel,
							path: sourcePath,
							clickable: true,
						},
						...(processFormCaseNo
							? [
									{
										id: `process-origin-case-${processFormCaseNo}`,
										label: processFormCaseNo,
										clickable: false,
									},
								]
							: []),
					];
				}
			}
		}

		if (normalized === 'solutions-catalog' || normalized === 'solution-catalog') {
			return [
				{
					id: 'process-origin-create',
					label: 'Create',
					path: createSourcePath,
					clickable: true,
				},
			];
		}

		if (normalized === 'create') {
			const createTabKey = getCreateTabKey(source.includes('?') ? source.split('?')[1] : '');
			return [
				{
					id: 'process-origin-create',
					label: 'Create',
					path: createSourcePath,
					clickable: true,
				},
				{
					id: `process-origin-create-tab-${createTabKey}`,
					label: getCreateTabLabel(source.includes('?') ? source.split('?')[1] : ''),
					path: createSourcePath,
					clickable: true,
				},
				...(processFormCaseNo
					? [
							{
								id: `process-origin-case-${processFormCaseNo}`,
								label: processFormCaseNo,
								clickable: false,
							},
						]
					: []),
			];
		}

		const sourceSegments = sourceKey.split('/').filter(Boolean);
		const genericItems = sourceSegments.map((segment, index) => ({
			id: `process-origin-generic-${segment}-${index}`,
			label: formatSegmentLabel(segment),
			path: `/${sourceSegments.slice(0, index + 1).join('/')}`,
			clickable: true,
		}));

		return processFormCaseNo
			? [
					...genericItems,
					{
						id: `process-origin-case-${processFormCaseNo}`,
						label: processFormCaseNo,
						clickable: false,
					},
				]
			: genericItems;
	}, [menuItems, pathname, processFormCaseNo, search, state?.pathname]);

	const staticCreateBreadcrumbItems = useMemo(() => {
		const segments = pathname.split('/').filter(Boolean);
		if (segments[0] !== 'static' || !segments[1]) return [];

		// Create flow static URLs can be:
		// /static/{slug}/{encodedPayload}
		// /static/{slug}/create|edit|details
		const actionSegment = segments[2]?.toLowerCase?.() ?? '';
		const hasEncodedTail = Boolean(segments[2] && parseEncodedPayloadSegment(segments[2]));
		const isStaticActionPath = ['create', 'edit', 'details'].includes(actionSegment);
		if (!hasEncodedTail && !isStaticActionPath) return [];

		const staticSlug = segments[1];
		const sourceFromState = typeof state?.pathname === 'string' ? state.pathname : '';
		const sourceFromFallback = getStoredCreateSourcePath() || (sessionStorage.getItem('breadcrumb:lastNonProcessPath') ?? '');
		const source = sourceFromState || sourceFromFallback;
		const { isCreateSource, createSourcePath } = resolveCreateSourcePath(source);
		const createTabKey = getCreateTabKey(source.includes('?') ? source.split('?')[1] : '');

		return [
			{
				id: 'static-create-root',
				label: 'Create',
				path: createSourcePath,
				clickable: true,
			},
			...(isCreateSource
				? [
						{
							id: `static-create-tab-${createTabKey}`,
							label: getCreateTabLabel(source.includes('?') ? source.split('?')[1] : ''),
							path: createSourcePath,
							clickable: true,
						},
					]
				: []),
			{
				id: `static-create-${staticSlug}`,
				label: formatSegmentLabel(staticSlug),
				path: `${pathname}${search}`,
				clickable: true,
			},
		];
	}, [pathname, search, state?.pathname]);

	const caseIdentityCreateBreadcrumbItems = useMemo(() => {
		const segments = pathname.split('/').filter(Boolean);
		if (segments[0] !== 'case-identity' || !segments[1]) return [];

		// URLs like /case-identity/{encodedPayload} also originate from create flow.
		const payload = parseEncodedPayloadSegment(segments[1]);
		if (!payload) return [];

		const decodedLabel = getPayloadLabel(payload);
		const sourceFromState = typeof state?.pathname === 'string' ? state.pathname : '';
		const sourceFromFallback = getStoredCreateSourcePath() || (sessionStorage.getItem('breadcrumb:lastNonProcessPath') ?? '');
		const source = sourceFromState || sourceFromFallback;
		const { isCreateSource, createSourcePath } = resolveCreateSourcePath(source);
		const createTabKey = getCreateTabKey(source.includes('?') ? source.split('?')[1] : '');

		return [
			{
				id: 'case-identity-create-root',
				label: 'Create',
				path: createSourcePath,
				clickable: true,
			},
			...(isCreateSource
				? [
						{
							id: `case-identity-create-tab-${createTabKey}`,
							label: getCreateTabLabel(source.includes('?') ? source.split('?')[1] : ''),
							path: createSourcePath,
							clickable: true,
						},
					]
				: []),
			{
				id: 'case-identity-create-leaf',
				label: decodedLabel ?? 'Case Identity',
				path: `${pathname}${search}`,
				clickable: true,
			},
		];
	}, [pathname, search, state?.pathname]);

	const caseConsoleCreateBreadcrumbItems = useMemo(() => {
		const segments = pathname.split('/').filter(Boolean);
		if (segments[0] !== 'case-console' || !segments[1]) return [];

		const actionSegment = segments[1]?.toLowerCase?.() ?? '';
		const isConsoleActionPath = ['create', 'edit', 'details'].includes(actionSegment);
		const payload = parseEncodedPayloadSegment(segments[1]);
		if (!payload && !isConsoleActionPath) return [];

		const decodedLabel = payload ? getPayloadLabel(payload) : null;
		const sourceFromState = typeof state?.pathname === 'string' ? state.pathname : '';
		const sourceFromFallback = getStoredCreateSourcePath() || (sessionStorage.getItem('breadcrumb:lastNonProcessPath') ?? '');
		const source = sourceFromState || sourceFromFallback;
		const { isCreateSource, createSourcePath } = resolveCreateSourcePath(source);
		const createTabKey = getCreateTabKey(source.includes('?') ? source.split('?')[1] : '');

		return [
			{
				id: 'case-console-create-root',
				label: 'Create',
				path: createSourcePath,
				clickable: true,
			},
			...(isCreateSource
				? [
						{
							id: `case-console-create-tab-${createTabKey}`,
							label: getCreateTabLabel(source.includes('?') ? source.split('?')[1] : ''),
							path: createSourcePath,
							clickable: true,
						},
					]
				: []),
			{
				id: 'case-console-create-leaf',
				label: decodedLabel ?? 'Case Console',
				path: `${pathname}${search}`,
				clickable: true,
			},
		];
	}, [pathname, search, state?.pathname]);

	const routeBreadcrumbItems = useMemo(() => {
		if (!Array.isArray(routeMatches) || routeMatches.length === 0) return [];

		const visibleMatches = routeMatches.filter((match) => {
			if (!match?.route) return false;
			return Boolean(match.route.path || match.route.handle?.breadcrumb);
		});
		const pathnameSegments = pathname.split('/').filter(Boolean);
		const isControlsNestedRoute = pathnameSegments[0] === 'controls' && pathnameSegments.length >= 3;

		return visibleMatches.reduce((items, match, index) => {
			const breadcrumbHandle = match.route?.handle?.breadcrumb;
			let label = null;
			let path = `${match.pathname}${index === visibleMatches.length - 1 ? search : ''}`;

			if (isControlsNestedRoute && match.route?.path === ':id') {
				if (index === visibleMatches.length - 1) {
					return items;
				}

				const controlsLabelSegment = pathnameSegments[1];
				if (!controlsLabelSegment) return items;

				label = formatSegmentLabel(controlsLabelSegment);
				path = `${pathname}${search}`;
			} else if (typeof breadcrumbHandle === 'function') {
				label = breadcrumbHandle(match);
			} else if (typeof breadcrumbHandle === 'string') {
				label = breadcrumbHandle;
			} else {
				const routePath = match.route?.path;
				if (!routePath) return items;

				const routeSegments = routePath.split('/').filter(Boolean);
				const lastRouteSegment = routeSegments[routeSegments.length - 1];
				if (!lastRouteSegment || lastRouteSegment === '*') return items;

				if (lastRouteSegment.startsWith(':')) {
					const paramKey = lastRouteSegment.slice(1);
					const paramValue = match.params?.[paramKey];
					if (!paramValue) return items;

					if (paramKey.toLowerCase() === 'module') {
						const moduleOption = contextOptions.find((option) => option?.name?.toLowerCase?.() === paramValue?.toLowerCase?.());
						label = moduleOption?.title ?? formatSegmentLabel(paramValue);
					} else {
						const decodedLabel = getDecodedSegmentLabel(paramValue);
						if (isTechnicalSegment(paramValue) && !decodedLabel) return items;
						label = decodedLabel ?? formatSegmentLabel(paramValue);
					}
				} else {
					label = formatSegmentLabel(lastRouteSegment);
				}
			}

			if (!label) return items;

			items.push({
				id: `route-${match.id ?? `${match.pathname}-${index}`}`,
				label,
				path,
				clickable: true,
			});

			return items;
		}, []);
	}, [contextOptions, routeMatches, search]);

	const decorateWithMenuGroupPrefix = (items) => {
		if (menuGroupPrefixItems.length === 0 || items.length === 0) return items;
		const firstVisibleItem = items[0];
		if (!firstVisibleItem?.label) return items;

		const firstSegmentLabel = formatSegmentLabel(menuScopedSegments[0] ?? '');
		if (firstVisibleItem.label.toLowerCase() !== firstSegmentLabel.toLowerCase()) return items;

		return [...menuGroupPrefixItems, ...items];
	};

	const breadcrumbItems =
		processFormOriginBreadcrumbItems.length > 0
			? processFormOriginBreadcrumbItems
			: createPageBreadcrumbItems.length > 0
				? createPageBreadcrumbItems
				: reportsPageBreadcrumbItems.length > 0
					? reportsPageBreadcrumbItems
					: reportDetailBreadcrumbItems.length > 0
						? reportDetailBreadcrumbItems
						: staticCreateBreadcrumbItems.length > 0
							? staticCreateBreadcrumbItems
							: caseIdentityCreateBreadcrumbItems.length > 0
								? caseIdentityCreateBreadcrumbItems
								: caseConsoleCreateBreadcrumbItems.length > 0
									? caseConsoleCreateBreadcrumbItems
									: routeBreadcrumbItems.length > 0
										? pathname.startsWith('/controls/')
											? decorateWithMenuGroupPrefix(pathBreadcrumbItems)
											: decorateWithMenuGroupPrefix(routeBreadcrumbItems)
										: decorateWithMenuGroupPrefix(pathBreadcrumbItems);

	useEffect(() => {
		const sourceFromState = typeof state?.pathname === 'string' ? state.pathname : '';
		const { isCreateSource, createSourcePath } = resolveCreateSourcePath(sourceFromState);
		if (isCreateSource) {
			sessionStorage.setItem('breadcrumb:createSourcePath', createSourcePath);
		}
	}, [state?.pathname]);

	useEffect(() => {
		const sourceFromState = typeof state?.pathname === 'string' ? state.pathname : '';
		const { isReportSource, reportSourcePath } = resolveReportSourcePath(sourceFromState);
		if (isReportSource) {
			sessionStorage.setItem('breadcrumb:reportSourcePath', reportSourcePath);
			return;
		}

		if (pathname === '/reports') {
			sessionStorage.setItem('breadcrumb:reportSourcePath', `${pathname}${search}`);
		}
	}, [pathname, search, state?.pathname]);

	useEffect(() => {
		if (pathname === '/process-form') return;
		sessionStorage.setItem('breadcrumb:lastNonProcessPath', `${pathname}${search}`);
	}, [pathname, search]);

	useEffect(() => {
		const syncProcessFormCaseNo = () => {
			const storedStageId = sessionStorage.getItem('breadcrumb:processFormStageId') ?? '';
			const activeStageId = String(state?.stageId ?? '');
			if (!activeStageId || storedStageId !== activeStageId) {
				setProcessFormCaseNo('');
				return;
			}

			setProcessFormCaseNo(sessionStorage.getItem('breadcrumb:processFormCaseNo') ?? '');
		};

		syncProcessFormCaseNo();
		window.addEventListener('breadcrumb:process-form-case-no', syncProcessFormCaseNo);

		return () => {
			window.removeEventListener('breadcrumb:process-form-case-no', syncProcessFormCaseNo);
		};
	}, [state?.stageId]);

	useEffect(() => {
		const container = breadcrumbScrollRef.current;
		if (!container) return;

		const frameId = requestAnimationFrame(() => {
			container.scrollLeft = container.scrollWidth;
		});

		return () => cancelAnimationFrame(frameId);
	}, [pathname, search, breadcrumbItems.length]);

	const startBreadcrumbDrag = (event) => {
		if (!breadcrumbScrollRef.current) return;
		breadcrumbDragRef.current = {
			isDragging: true,
			startX: event.pageX,
			startScrollLeft: breadcrumbScrollRef.current.scrollLeft,
			didDrag: false,
		};
	};

	const moveBreadcrumbDrag = (event) => {
		const container = breadcrumbScrollRef.current;
		if (!container || !breadcrumbDragRef.current.isDragging) return;

		const deltaX = event.pageX - breadcrumbDragRef.current.startX;
		if (Math.abs(deltaX) > 4) {
			breadcrumbDragRef.current.didDrag = true;
		}

		container.scrollLeft = breadcrumbDragRef.current.startScrollLeft - deltaX;
	};

	const stopBreadcrumbDrag = () => {
		breadcrumbDragRef.current.isDragging = false;
	};

	const onBreadcrumbWheel = (event) => {
		const container = breadcrumbScrollRef.current;
		if (!container) return;

		const horizontalDelta = Math.abs(event.deltaX) > 0 ? event.deltaX : -event.deltaY;
		if (!horizontalDelta) return;

		container.scrollLeft += horizontalDelta;
		if (event.cancelable) {
			event.preventDefault();
		}
	};

	const suppressClickAfterDrag = (event) => {
		if (!breadcrumbDragRef.current.didDrag) return;
		breadcrumbDragRef.current.didDrag = false;
		event.preventDefault();
		event.stopPropagation();
	};

	return (
		<Box
			ref={breadcrumbScrollRef}
			onWheel={onBreadcrumbWheel}
			onMouseDown={startBreadcrumbDrag}
			onMouseMove={moveBreadcrumbDrag}
			onMouseUp={stopBreadcrumbDrag}
			onMouseLeave={stopBreadcrumbDrag}
			onClickCapture={suppressClickAfterDrag}
			sx={{
				mt: 1.25,
				width: '100%',
				maxWidth: '100%',
				minWidth: 0,
				display: 'flex',
				flexWrap: 'nowrap',
				alignItems: 'center',
				gap: 0.75,
				overflowX: 'auto',
				overflowY: 'hidden',
				scrollbarWidth: 'none',
				msOverflowStyle: 'none',
				fontSize: '0.688rem',
				color: '#64748B',
				cursor: 'grab',
				userSelect: 'none',
				'&::-webkit-scrollbar': {
					display: 'none',
				},
				'&:active': {
					cursor: 'grabbing',
				},
			}}
		>
			{breadcrumbItems.map((item, index) => {
				const isActive = index === breadcrumbItems.length - 1;
				return (
					<Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: '0 0 auto' }}>
						{item.clickable ? (
							<ButtonBase
								onClick={() =>
									navigate(item.path, {
										state: item.path?.startsWith('/controls/') ? state : undefined,
									})
								}
								sx={{
									px: 1,
									py: 0.25,
									borderRadius: '999px',
									backgroundColor: isActive ? '#EFF6FF' : '#F1F5F9',
									border: isActive ? '1px solid #DBEAFE' : '1px solid #E2E8F0',
									color: isActive ? '#1D4ED8' : '#64748B',
									fontSize: '0.688rem',
									lineHeight: 1.4,
									'&:hover': {
										backgroundColor: isActive ? '#DBEAFE' : '#E2E8F0',
									},
								}}
							>
								{item.label}
							</ButtonBase>
						) : (
							<Box
								component="span"
								sx={{
									px: 1,
									py: 0.25,
									borderRadius: '999px',
									backgroundColor: isActive ? '#EFF6FF' : '#F1F5F9',
									border: isActive ? '1px solid #DBEAFE' : '1px solid #E2E8F0',
									color: isActive ? '#1D4ED8' : '#64748B',
									fontSize: '0.688rem',
									lineHeight: 1.4,
								}}
							>
								{item.label}
							</Box>
						)}
						{index < breadcrumbItems.length - 1 && <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>/</Typography>}
					</Box>
				);
			})}
		</Box>
	);
}

export default BreadcrumbTrail;
